import * as React from 'react'
import { toast } from 'sonner'
import { getArtifactById, getDownloadUrl } from '@/client/sdk.gen'
import type { ArtifactResponse, ArtifactStatusEnum } from '@/client/types.gen'
import { useSocket } from '@/context'
import { useTranslation } from '@/hooks/useTranslation'
import type { ArtifactStatusUpdateEvent } from '@/types/websocket'
import { extractErrorMessage } from '@/utils/error'

function isTerminalStatus(status: ArtifactStatusEnum): boolean {
	return status === 'completed' || status === 'failed'
}

export interface UseArtifactPreviewResult {
	artifact: ArtifactResponse | null
	loading: boolean
	error: string | null
	retry: () => Promise<void>
	handleDownload: () => Promise<void>
	downloading: boolean
}

export function useArtifactPreview(
	projectId: string | undefined,
	artifactId: string | null | undefined,
	_refreshTrigger?: number
): UseArtifactPreviewResult {
	const { t } = useTranslation()
	const { socket, connected } = useSocket()
	const [artifact, setArtifact] = React.useState<ArtifactResponse | null>(null)
	const [loading, setLoading] = React.useState(false)
	const [error, setError] = React.useState<string | null>(null)
	const [downloading, setDownloading] = React.useState(false)

	const fetchArtifactDetails = React.useCallback(
		async (isInitialLoad = false, isCancelled?: () => boolean): Promise<void> => {
			if (!projectId || !artifactId) return
			if (isInitialLoad) {
				setLoading(true)
				setError(null)
			}
			try {
				// With throwOnError: true, 4xx/5xx (e.g. "Artifact not found") throw the parsed body { detail }.
				const response = await getArtifactById({
					path: { project_id: projectId, artifact_id: artifactId },
				})
				if (isCancelled?.()) return
				if (response.error) {
					console.error('Artifact preview fetch failed:', response.error)
					setError(
						extractErrorMessage(response.error, t('pages.projectDetail.artifactPreview.errorLoading'))
					)
					setArtifact(null)
				} else if (response.data) {
					setArtifact(response.data)
				}
			} catch (err) {
				if (isCancelled?.()) return
				console.error('Artifact preview fetch failed:', err)
				setError(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorLoading')))
				setArtifact(null)
			} finally {
				if (isInitialLoad && !isCancelled?.()) {
					setLoading(false)
				}
			}
		},
		[projectId, artifactId, t]
	)

	// Fetch when artifactId becomes set or refreshTrigger changes (with cancellation to avoid race conditions)
	React.useEffect(() => {
		if (!projectId || !artifactId) {
			setArtifact(null)
			setError(null)
			return
		}
		let cancelled = false
		const checkCancelled = () => cancelled
		fetchArtifactDetails(true, checkCancelled).catch((err) => {
			if (cancelled) return
			console.error('Artifact preview fetch failed:', err)
			setError(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorLoading')))
			setArtifact(null)
			setLoading(false)
		})
		return () => {
			cancelled = true
		}
	}, [projectId, artifactId, fetchArtifactDetails, t])

	// Refetch when socket reports status update for this artifact
	React.useEffect(() => {
		if (
			!socket ||
			!connected ||
			!projectId ||
			!artifactId ||
			!artifact ||
			isTerminalStatus(artifact.status)
		)
			return
		const handleStatusUpdate = (data: ArtifactStatusUpdateEvent) => {
			if (data.project_id === projectId && data.artifact_id === artifactId) {
				fetchArtifactDetails(false).catch((err) => {
					console.error('Artifact preview fetch failed:', err)
					setError(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorLoading')))
					setArtifact(null)
				})
			}
		}
		socket.on('artifact:status:update', handleStatusUpdate)
		return () => {
			socket.off('artifact:status:update', handleStatusUpdate)
		}
	}, [socket, connected, projectId, artifactId, artifact, fetchArtifactDetails, t])

	const retry = React.useCallback(() => fetchArtifactDetails(true), [fetchArtifactDetails])

	const handleDownload = React.useCallback(async () => {
		if (!projectId || !artifactId) return
		setDownloading(true)
		try {
			const response = await getDownloadUrl({
				path: { project_id: projectId, artifact_id: artifactId },
			})
			if (response.error) {
				toast.error(
					extractErrorMessage(response.error, t('pages.projectDetail.artifactPreview.errorDownloading'))
				)
			} else if (response.data?.download_url) {
				window.open(response.data.download_url, '_blank', 'noopener,noreferrer')
			}
		} catch (err) {
			toast.error(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorDownloading')))
		} finally {
			setDownloading(false)
		}
	}, [projectId, artifactId, t])

	return {
		artifact,
		loading,
		error,
		retry,
		handleDownload,
		downloading,
	}
}
